/**
 * The hundred commonest 汉字, and phrases that put them to work.
 *
 * Three things live here, doing different jobs:
 *
 * `TOP_CHARS_ZH` is the target — the 100 most frequent single characters in
 * modern Mandarin, in rank order, following Jun Da's Modern Chinese Character
 * Frequency List. Coverage is measured against this, and because every entry is
 * exactly one character the measurement is exact rather than fuzzy.
 *
 * `COMMON_WORDS_ZH` exists only for display. 什么 is two of the hundred
 * characters, but drawing it as 什 + 么 would put a word boundary through the
 * middle of a word — so the highlighter matches longest-first and these win.
 * They are not additional targets; they change how the target is drawn.
 *
 * `COVERAGE_PHRASES` is the content. Each is a natural sentence carrying five
 * or six of the tracked characters at once, the way real sentences do, and
 * between them they cover all hundred. Deliberately NOT one phrase per
 * character: a hundred disconnected one-liners is a vocabulary list wearing a
 * disguise, and this product's whole argument is that a word you have only ever
 * seen glossed is a word you cannot use.
 *
 * Nothing here is drilled or scheduled. The refresher sits outside the island
 * system on purpose — a hundred entries in the review queue would bury the six
 * or seven islands that are the actual product.
 *
 * Generated, then verified mechanically: every phrase has exactly one pinyin
 * syllable per Chinese character (the rule the grader enforces), every entry in
 * the hundred is a single character, ranks run 1..100 with no gaps or repeats,
 * all 100 are covered, and every phrase carries tone diacritics.
 */

export interface FrequentChar {
  /** 1 is the commonest. */
  rank: number
  /** Exactly one simplified character. */
  hanzi: string
  /** Tone-marked. Several readings are separated by a slash: "le / liǎo". */
  pinyin: string
  english: string
  /** pron, v, adj, part, prep, n, num, mw, conj, adv. */
  pos?: string
}

export interface CommonWord {
  hanzi: string
  pinyin: string
  english: string
}

export interface CoveragePhrase {
  hanzi: string
  /** Tone-marked, one syllable per Chinese character, punctuation untranscribed. */
  pinyin: string
  english: string
  /** One sentence, only where the phrase contains a real trap. Usually empty. */
  note: string
  /** Which of the hundred this phrase contains. Measured from the text, not claimed. */
  covers: string[]
}

export const TOP_CHARS_ZH: FrequentChar[] = [
  { rank: 1, hanzi: '的', pinyin: 'de / dí / dì', english: 'possessive/attributive particle', pos: 'part' },
  { rank: 2, hanzi: '一', pinyin: 'yī', english: 'one; a', pos: 'num' },
  { rank: 3, hanzi: '是', pinyin: 'shì', english: 'to be; yes', pos: 'v' },
  { rank: 4, hanzi: '不', pinyin: 'bù', english: 'not; no', pos: 'adv' },
  { rank: 5, hanzi: '了', pinyin: 'le / liǎo', english: 'completed-action particle; to finish', pos: 'part' },
  { rank: 6, hanzi: '在', pinyin: 'zài', english: 'at, in; to be located; -ing', pos: 'prep' },
  { rank: 7, hanzi: '人', pinyin: 'rén', english: 'person; people', pos: 'n' },
  { rank: 8, hanzi: '有', pinyin: 'yǒu', english: 'to have; there is', pos: 'v' },
  { rank: 9, hanzi: '我', pinyin: 'wǒ', english: 'I; me', pos: 'pron' },
  { rank: 10, hanzi: '他', pinyin: 'tā', english: 'he; him', pos: 'pron' },
  { rank: 11, hanzi: '这', pinyin: 'zhè / zhèi', english: 'this', pos: 'pron' },
  { rank: 12, hanzi: '个', pinyin: 'gè', english: 'general measure word', pos: 'mw' },
  { rank: 13, hanzi: '们', pinyin: 'men', english: 'plural suffix for people', pos: 'part' },
  { rank: 14, hanzi: '中', pinyin: 'zhōng / zhòng', english: 'middle, center; China', pos: 'n' },
  { rank: 15, hanzi: '来', pinyin: 'lái', english: 'to come', pos: 'v' },
  { rank: 16, hanzi: '上', pinyin: 'shàng', english: 'above, on; previous; to go up', pos: 'n' },
  { rank: 17, hanzi: '大', pinyin: 'dà', english: 'big; great', pos: 'adj' },
  { rank: 18, hanzi: '为', pinyin: 'wèi / wéi', english: 'for; to act as, to be', pos: 'prep' },
  { rank: 19, hanzi: '和', pinyin: 'hé', english: 'and; with; harmony', pos: 'conj' },
  { rank: 20, hanzi: '国', pinyin: 'guó', english: 'country; nation', pos: 'n' },
  { rank: 21, hanzi: '地', pinyin: 'dì / de', english: 'earth, land; adverbial particle', pos: 'n' },
  { rank: 22, hanzi: '到', pinyin: 'dào', english: 'to arrive; to, until', pos: 'v' },
  { rank: 23, hanzi: '以', pinyin: 'yǐ', english: 'with, by means of; in order to', pos: 'prep' },
  { rank: 24, hanzi: '说', pinyin: 'shuō / shuì', english: 'to speak; to say', pos: 'v' },
  { rank: 25, hanzi: '时', pinyin: 'shí', english: 'time; hour; when', pos: 'n' },
  { rank: 26, hanzi: '要', pinyin: 'yào / yāo', english: 'to want; must; important', pos: 'v' },
  { rank: 27, hanzi: '就', pinyin: 'jiù', english: 'then; at once; only', pos: 'adv' },
  { rank: 28, hanzi: '出', pinyin: 'chū', english: 'to go out; to produce', pos: 'v' },
  { rank: 29, hanzi: '会', pinyin: 'huì / kuài', english: 'can, will; meeting', pos: 'v' },
  { rank: 30, hanzi: '可', pinyin: 'kě', english: 'can, may; but', pos: 'v' },
  { rank: 31, hanzi: '也', pinyin: 'yě', english: 'also; too', pos: 'adv' },
  { rank: 32, hanzi: '你', pinyin: 'nǐ', english: 'you (singular)', pos: 'pron' },
  { rank: 33, hanzi: '对', pinyin: 'duì', english: 'correct; toward; pair', pos: 'adj' },
  { rank: 34, hanzi: '生', pinyin: 'shēng', english: 'to be born; life; raw', pos: 'v' },
  { rank: 35, hanzi: '能', pinyin: 'néng', english: 'can; to be able to; energy', pos: 'v' },
  { rank: 36, hanzi: '而', pinyin: 'ér', english: 'and; but; yet', pos: 'conj' },
  { rank: 37, hanzi: '子', pinyin: 'zi / zǐ', english: 'noun suffix; child, son', pos: 'part' },
  { rank: 38, hanzi: '那', pinyin: 'nà / nèi', english: 'that; then', pos: 'pron' },
  { rank: 39, hanzi: '得', pinyin: 'de / dé / děi', english: 'complement particle; to get; must', pos: 'part' },
  { rank: 40, hanzi: '于', pinyin: 'yú', english: 'in, at, to; than', pos: 'prep' },
  { rank: 41, hanzi: '着', pinyin: 'zhe / zhuó / zháo', english: 'durative particle (-ing); to touch', pos: 'part' },
  { rank: 42, hanzi: '下', pinyin: 'xià', english: 'below, under; next; to descend', pos: 'n' },
  { rank: 43, hanzi: '自', pinyin: 'zì', english: 'self; from, since', pos: 'pron' },
  { rank: 44, hanzi: '之', pinyin: 'zhī', english: 'classical possessive particle; it', pos: 'part' },
  { rank: 45, hanzi: '年', pinyin: 'nián', english: 'year', pos: 'n' },
  { rank: 46, hanzi: '过', pinyin: 'guò', english: 'to pass; experiential particle', pos: 'v' },
  { rank: 47, hanzi: '发', pinyin: 'fā / fà', english: 'to send out, to issue; hair', pos: 'v' },
  { rank: 48, hanzi: '后', pinyin: 'hòu', english: 'after; behind; back', pos: 'n' },
  { rank: 49, hanzi: '作', pinyin: 'zuò', english: 'to do; to make; work', pos: 'v' },
  { rank: 50, hanzi: '里', pinyin: 'lǐ', english: 'inside; in', pos: 'n' },
  { rank: 51, hanzi: '用', pinyin: 'yòng', english: 'to use; use', pos: 'v' },
  { rank: 52, hanzi: '道', pinyin: 'dào', english: 'way, road; to say', pos: 'n' },
  { rank: 53, hanzi: '行', pinyin: 'xíng / háng', english: 'to walk, OK; row, trade', pos: 'v' },
  { rank: 54, hanzi: '所', pinyin: 'suǒ', english: 'place; nominalizing particle', pos: 'part' },
  { rank: 55, hanzi: '然', pinyin: 'rán', english: 'so; thus; -ly', pos: 'adv' },
  { rank: 56, hanzi: '家', pinyin: 'jiā', english: 'home; family; specialist', pos: 'n' },
  { rank: 57, hanzi: '种', pinyin: 'zhǒng / zhòng', english: 'kind, type, seed; to plant', pos: 'mw' },
  { rank: 58, hanzi: '事', pinyin: 'shì', english: 'matter; affair; thing', pos: 'n' },
  { rank: 59, hanzi: '成', pinyin: 'chéng', english: 'to become; to succeed', pos: 'v' },
  { rank: 60, hanzi: '方', pinyin: 'fāng', english: 'square; direction; method', pos: 'n' },
  { rank: 61, hanzi: '多', pinyin: 'duō', english: 'many; much; more', pos: 'adj' },
  { rank: 62, hanzi: '经', pinyin: 'jīng', english: 'to pass through; classic; already', pos: 'v' },
  { rank: 63, hanzi: '么', pinyin: 'me', english: 'interrogative suffix (in 什么, 怎么)', pos: 'part' },
  { rank: 64, hanzi: '去', pinyin: 'qù', english: 'to go; to leave', pos: 'v' },
  { rank: 65, hanzi: '法', pinyin: 'fǎ', english: 'law; method', pos: 'n' },
  { rank: 66, hanzi: '学', pinyin: 'xué', english: 'to study; learning', pos: 'v' },
  { rank: 67, hanzi: '如', pinyin: 'rú', english: 'if; like, as', pos: 'conj' },
  { rank: 68, hanzi: '都', pinyin: 'dōu / dū', english: 'all; both; capital city', pos: 'adv' },
  { rank: 69, hanzi: '同', pinyin: 'tóng', english: 'same; together; with', pos: 'adj' },
  { rank: 70, hanzi: '现', pinyin: 'xiàn', english: 'now, present; to appear', pos: 'n' },
  { rank: 71, hanzi: '当', pinyin: 'dāng / dàng', english: 'when; to serve as; proper', pos: 'prep' },
  { rank: 72, hanzi: '没', pinyin: 'méi / mò', english: 'not have; not; to sink', pos: 'adv' },
  { rank: 73, hanzi: '动', pinyin: 'dòng', english: 'to move; to act', pos: 'v' },
  { rank: 74, hanzi: '面', pinyin: 'miàn', english: 'face; side; surface; noodles', pos: 'n' },
  { rank: 75, hanzi: '起', pinyin: 'qǐ', english: 'to rise; to begin', pos: 'v' },
  { rank: 76, hanzi: '看', pinyin: 'kàn / kān', english: 'to look at; to watch; to guard', pos: 'v' },
  { rank: 77, hanzi: '定', pinyin: 'dìng', english: 'to decide; fixed; surely', pos: 'v' },
  { rank: 78, hanzi: '天', pinyin: 'tiān', english: 'sky; day; heaven', pos: 'n' },
  { rank: 79, hanzi: '分', pinyin: 'fēn / fèn', english: 'to divide; minute; part', pos: 'v' },
  { rank: 80, hanzi: '还', pinyin: 'hái / huán', english: 'still, also; to return', pos: 'adv' },
  { rank: 81, hanzi: '进', pinyin: 'jìn', english: 'to enter; to advance', pos: 'v' },
  { rank: 82, hanzi: '好', pinyin: 'hǎo / hào', english: 'good; well; to like', pos: 'adj' },
  { rank: 83, hanzi: '小', pinyin: 'xiǎo', english: 'small; little', pos: 'adj' },
  { rank: 84, hanzi: '部', pinyin: 'bù', english: 'part; department; ministry', pos: 'n' },
  { rank: 85, hanzi: '其', pinyin: 'qí', english: 'his, her, its, their; that', pos: 'pron' },
  { rank: 86, hanzi: '些', pinyin: 'xiē', english: 'some; a few', pos: 'mw' },
  { rank: 87, hanzi: '主', pinyin: 'zhǔ', english: 'main; master; owner', pos: 'adj' },
  { rank: 88, hanzi: '样', pinyin: 'yàng', english: 'kind, manner; appearance', pos: 'n' },
  { rank: 89, hanzi: '理', pinyin: 'lǐ', english: 'reason, logic; to manage', pos: 'n' },
  { rank: 90, hanzi: '心', pinyin: 'xīn', english: 'heart; mind', pos: 'n' },
  { rank: 91, hanzi: '她', pinyin: 'tā', english: 'she; her', pos: 'pron' },
  { rank: 92, hanzi: '本', pinyin: 'běn', english: 'root, origin; this; mw for books', pos: 'mw' },
  { rank: 93, hanzi: '前', pinyin: 'qián', english: 'front; before; ago', pos: 'n' },
  { rank: 94, hanzi: '开', pinyin: 'kāi', english: 'to open; to start; to drive', pos: 'v' },
  { rank: 95, hanzi: '但', pinyin: 'dàn', english: 'but; however; only', pos: 'conj' },
  { rank: 96, hanzi: '因', pinyin: 'yīn', english: 'because; cause, reason', pos: 'conj' },
  { rank: 97, hanzi: '只', pinyin: 'zhǐ / zhī', english: 'only, just; mw for animals', pos: 'adv' },
  { rank: 98, hanzi: '从', pinyin: 'cóng', english: 'from; since; to follow', pos: 'prep' },
  { rank: 99, hanzi: '想', pinyin: 'xiǎng', english: 'to think; to want; to miss', pos: 'v' },
  { rank: 100, hanzi: '实', pinyin: 'shí', english: 'real; solid; actual', pos: 'adj' },
]

/**
 * Multi-character words the highlighter treats as single units.
 *
 * Not extra targets — every character in these is already one of the hundred.
 * They exist so 什么 is drawn as 什么 rather than as two adjacent characters.
 */
export const COMMON_WORDS_ZH: CommonWord[] = [
  { hanzi: '什么', pinyin: 'shénme', english: 'what' },
  { hanzi: '为什么', pinyin: 'wèishénme', english: 'why' },
  { hanzi: '怎么', pinyin: 'zěnme', english: 'how; how come' },
  { hanzi: '这么', pinyin: 'zhème', english: 'so; this much; like this' },
  { hanzi: '那么', pinyin: 'nàme', english: 'so; in that case' },
  { hanzi: '我们', pinyin: 'wǒmen', english: 'we; us' },
  { hanzi: '你们', pinyin: 'nǐmen', english: 'you (plural)' },
  { hanzi: '他们', pinyin: 'tāmen', english: 'they; them' },
  { hanzi: '她们', pinyin: 'tāmen', english: 'they; them (all female)' },
  { hanzi: '人们', pinyin: 'rénmen', english: 'people' },
  { hanzi: '这个', pinyin: 'zhège', english: 'this; this one' },
  { hanzi: '那个', pinyin: 'nàge', english: 'that; that one' },
  { hanzi: '这些', pinyin: 'zhèxiē', english: 'these' },
  { hanzi: '那些', pinyin: 'nàxiē', english: 'those' },
  { hanzi: '这样', pinyin: 'zhèyàng', english: 'this way; like this' },
  { hanzi: '一样', pinyin: 'yíyàng', english: 'the same; alike' },
  { hanzi: '时候', pinyin: 'shíhou', english: 'time; moment; when' },
  { hanzi: '时间', pinyin: 'shíjiān', english: 'time; period of time' },
  { hanzi: '现在', pinyin: 'xiànzài', english: 'now; at present' },
  { hanzi: '以后', pinyin: 'yǐhòu', english: 'after; later; from now on' },
  { hanzi: '以前', pinyin: 'yǐqián', english: 'before; previously; ago' },
  { hanzi: '因为', pinyin: 'yīnwèi', english: 'because' },
  { hanzi: '所以', pinyin: 'suǒyǐ', english: 'so; therefore' },
  { hanzi: '但是', pinyin: 'dànshì', english: 'but; however' },
  { hanzi: '可以', pinyin: 'kěyǐ', english: 'can; may; be allowed to' },
  { hanzi: '可能', pinyin: 'kěnéng', english: 'maybe; possible; possibility' },
  { hanzi: '就是', pinyin: 'jiùshì', english: 'is exactly; precisely' },
  { hanzi: '不是', pinyin: 'búshì', english: 'is not; no' },
  { hanzi: '没有', pinyin: 'méiyǒu', english: 'not have; there isn\'t; didn\'t' },
  { hanzi: '知道', pinyin: 'zhīdào', english: 'to know' },
  { hanzi: '自己', pinyin: 'zìjǐ', english: 'oneself; one\'s own' },
  { hanzi: '一个', pinyin: 'yí gè', english: 'one (of something); a' },
  { hanzi: '一起', pinyin: 'yìqǐ', english: 'together' },
  { hanzi: '一定', pinyin: 'yídìng', english: 'certainly; definitely; fixed' },
  { hanzi: '中国', pinyin: 'Zhōngguó', english: 'China' },
  { hanzi: '大家', pinyin: 'dàjiā', english: 'everyone' },
  { hanzi: '起来', pinyin: 'qǐlái', english: 'to get up; to start (verb complement)' },
  { hanzi: '出来', pinyin: 'chūlái', english: 'to come out' },
  { hanzi: '地方', pinyin: 'dìfang', english: 'place; area' },
  { hanzi: '国家', pinyin: 'guójiā', english: 'country; nation; state' },
]

export const COVERAGE_PHRASES: CoveragePhrase[] = [
  {
    hanzi: '我们中国人不这么说。',
    pinyin: 'wǒ men zhōng guó rén bú zhè me shuō',
    english: 'We Chinese don\'t say it that way.',
    note: '不 is pronounced bú before the fourth-tone 这.',
    covers: ['不', '人', '我', '这', '们', '中', '国', '说', '么'],
  },
  {
    hanzi: '他上个月来了一次，住了三天。',
    pinyin: 'tā shàng gè yuè lái le yí cì zhù le sān tiān',
    english: 'He came once last month and stayed three days.',
    note: '一 becomes yí before the fourth-tone 次, and both 了 here mark completed actions rather than a change of state.',
    covers: ['一', '了', '他', '个', '来', '上', '天'],
  },
  {
    hanzi: '这是你们公司最大的项目吗？',
    pinyin: 'zhè shì nǐ men gōng sī zuì dà de xiàng mù ma',
    english: 'Is this your company\'s biggest project?',
    note: '',
    covers: ['的', '是', '这', '们', '大', '你'],
  },
  {
    hanzi: '外面有一个人在等你，快去吧。',
    pinyin: 'wài miàn yǒu yí gè rén zài děng nǐ kuài qù ba',
    english: 'There\'s someone outside waiting for you — go on.',
    note: '一 becomes yí before the fourth-tone measure word 个.',
    covers: ['一', '在', '人', '有', '个', '你', '去', '面'],
  },
  {
    hanzi: '为了孩子，他和太太搬到了国外。',
    pinyin: 'wèi le hái zi tā hé tài tai bān dào le guó wài',
    english: 'For the kids\' sake, he and his wife moved abroad.',
    note: 'The 了 in 为了 (wèi le, "for the sake of") is part of a fixed word, while the 了 in 搬到了 is the completed-action particle.',
    covers: ['了', '他', '为', '和', '国', '到', '子'],
  },
  {
    hanzi: '你说得对，那件事我也听说过。',
    pinyin: 'nǐ shuō de duì nà jiàn shì wǒ yě tīng shuō guo',
    english: 'You\'re right — I\'ve heard about that too.',
    note: '得 here is the neutral-tone complement particle de, not děi (must) or dé (to get).',
    covers: ['我', '说', '也', '你', '对', '那', '得', '过', '事'],
  },
  {
    hanzi: '时间一到，他们就会出发。',
    pinyin: 'shí jiān yí dào tā men jiù huì chū fā',
    english: 'The moment it\'s time, they\'ll set off.',
    note: '一 is pronounced yí before the fourth-tone 到.',
    covers: ['一', '他', '们', '到', '时', '就', '出', '会', '发'],
  },
  {
    hanzi: '关于孩子上学的事，你可以问问他。',
    pinyin: 'guān yú hái zi shàng xué de shì nǐ kě yǐ wèn wen tā',
    english: 'About the kids\' schooling, you can go ask him.',
    note: '子 in 孩子 is the neutral-tone suffix zi, never zǐ.',
    covers: ['的', '他', '上', '以', '可', '你', '子', '于', '事', '学'],
  },
  {
    hanzi: '这个地方我不熟，你要能带我去就好了。',
    pinyin: 'zhè ge dì fang wǒ bù shú nǐ yào néng dài wǒ qù jiù hǎo le',
    english: 'I don\'t know this area well; it\'d be great if you could take me.',
    note: '地 is dì in 地方 (a place), not the adverbial particle de, and 不 stays bù before second-tone 熟.',
    covers: ['不', '了', '我', '这', '个', '地', '要', '就', '你', '能', '方', '去', '好'],
  },
  {
    hanzi: '他这个人做事认真，而且生活也很简单。',
    pinyin: 'tā zhè ge rén zuò shì rèn zhēn ér qiě shēng huó yě hěn jiǎn dān',
    english: 'He takes his work seriously, and he lives simply too.',
    note: '',
    covers: ['人', '他', '这', '个', '也', '生', '而', '事'],
  },
  {
    hanzi: '这种事我自己知道怎么处理，不用你管。',
    pinyin: 'zhè zhǒng shì wǒ zì jǐ zhī dào zěn me chǔ lǐ bú yòng nǐ guǎn',
    english: 'I know how to handle this kind of thing myself — you don\'t need to get involved.',
    note: '不 is pronounced bú here because 用 is fourth tone.',
    covers: ['不', '我', '这', '你', '自', '用', '道', '种', '事', '么', '理'],
  },
  {
    hanzi: '他工作了三年，然后自己开了家公司。',
    pinyin: 'tā gōng zuò le sān nián rán hòu zì jǐ kāi le jiā gōng sī',
    english: 'He worked for three years, then opened his own company.',
    note: 'The first 了 marks a span of time rather than a finished action, and 开了家公司 drops the 一 before the measure word 家, as speech normally does.',
    covers: ['了', '他', '自', '年', '后', '作', '然', '家', '开'],
  },
  {
    hanzi: '这个方法我试过，行不通，下次别用了。',
    pinyin: 'zhè ge fāng fǎ wǒ shì guo xíng bù tōng xià cì bié yòng le',
    english: 'I\'ve tried this method — it doesn\'t work. Don\'t use it next time.',
    note: '过 here is the experiential marker, unstressed guo, not the verb guò; 不 stays bù before the first-tone 通.',
    covers: ['不', '了', '我', '这', '个', '下', '过', '用', '行', '方', '法'],
  },
  {
    hanzi: '会开完之后，材料我发到群里了。',
    pinyin: 'huì kāi wán zhī hòu cái liào wǒ fā dào qún lǐ le',
    english: 'After the meeting finished, I sent the materials to the group chat.',
    note: '',
    covers: ['了', '我', '到', '会', '之', '发', '后', '里', '开'],
  },
  {
    hanzi: '大家都忙着呢，所以这件事一直没办成。',
    pinyin: 'dà jiā dōu máng zhe ne suǒ yǐ zhè jiàn shì yì zhí méi bàn chéng',
    english: 'Everyone was busy, so this never actually got done.',
    note: '一直 is yì zhí — 一 shifts to yì before the second-tone 直 — and 着 is the unstressed durative zhe.',
    covers: ['一', '这', '大', '以', '着', '所', '家', '事', '成', '都', '没'],
  },
  {
    hanzi: '他学了这么多年，现在还没决定去哪。',
    pinyin: 'tā xué le zhè me duō nián xiàn zài hái méi jué dìng qù nǎ',
    english: 'He\'s studied for all these years and still hasn\'t decided where to go.',
    note: 'The 了 after 学 marks how long the studying went on, not that it finished.',
    covers: ['了', '在', '他', '这', '年', '多', '么', '去', '学', '现', '没', '定', '还'],
  },
  {
    hanzi: '这个办法我们都试过，看起来行不通。',
    pinyin: 'zhè ge bàn fǎ wǒ men dōu shì guò kàn qǐ lái xíng bù tōng',
    english: 'We\'ve all tried this method — it looks like it won\'t work.',
    note: '',
    covers: ['不', '我', '这', '个', '们', '来', '过', '行', '法', '都', '起', '看'],
  },
  {
    hanzi: '我已经三天没动过那些东西了。',
    pinyin: 'wǒ yǐ jīng sān tiān méi dòng guò nà xiē dōng xi le',
    english: 'I haven\'t touched that stuff in three days.',
    note: '没 negates the verb while the sentence-final 了 keeps the three-day gap true up to now.',
    covers: ['了', '我', '那', '过', '经', '没', '动', '天', '些'],
  },
  {
    hanzi: '如果大家意见不同，就当面把话说清楚。',
    pinyin: 'rú guǒ dà jiā yì jiàn bù tóng jiù dāng miàn bǎ huà shuō qīng chu',
    english: 'If people disagree, then say it clearly to each other\'s faces.',
    note: '',
    covers: ['不', '大', '说', '就', '家', '如', '同', '当', '面'],
  },
  {
    hanzi: '会开了两个小时，一分钟都没停。',
    pinyin: 'huì kāi le liǎng ge xiǎo shí yì fēn zhōng dōu méi tíng',
    english: 'The meeting ran two hours without stopping for a minute.',
    note: '两个小时 takes 两 rather than 二 for a counted amount, and 一 before the first-tone 分 is said yì.',
    covers: ['一', '了', '个', '时', '会', '都', '没', '分', '小', '开'],
  },
  {
    hanzi: '她从小就想开一家小书店。',
    pinyin: 'tā cóng xiǎo jiù xiǎng kāi yì jiā xiǎo shū diàn',
    english: 'Ever since she was little she\'s wanted to open a small bookshop.',
    note: '一 is yì here because the following 家 is first tone.',
    covers: ['一', '就', '家', '小', '她', '开', '从', '想'],
  },
  {
    hanzi: '其实这些道理她心里都明白。',
    pinyin: 'qí shí zhè xiē dào lǐ tā xīn li dōu míng bai',
    english: 'Honestly, she understands all of this perfectly well.',
    note: '',
    covers: ['这', '里', '道', '都', '其', '些', '理', '心', '她', '实'],
  },
  {
    hanzi: '这本书的前半部分写得很好。',
    pinyin: 'zhè běn shū de qián bàn bù fen xiě de hěn hǎo',
    english: 'The first half of this book is really well written.',
    note: 'Both 的 and 得 are de, but 的 links 书 to 前半部分 while 得 attaches the 很好 judgment to the verb 写.',
    covers: ['的', '这', '得', '分', '好', '部', '本', '前'],
  },
  {
    hanzi: '主要是因为下雨，我们只好早点进去。',
    pinyin: 'zhǔ yào shì yīn wèi xià yǔ wǒ men zhǐ hǎo zǎo diǎn jìn qù',
    english: 'Mainly because it was raining, we had no choice but to go in early.',
    note: '',
    covers: ['是', '我', '们', '为', '要', '下', '去', '进', '好', '主', '因', '只'],
  },
  {
    hanzi: '事情不是这样的，但我不想多说。',
    pinyin: 'shì qing bú shì zhè yàng de dàn wǒ bù xiǎng duō shuō',
    english: 'That\'s not how it happened, but I\'d rather not say more.',
    note: '不 shifts to bú before fourth-tone 是, yet stays bù before third-tone 想.',
    covers: ['的', '是', '不', '我', '这', '说', '事', '多', '样', '但', '想'],
  },
]

/**
 * What the highlighter matches against: the words, then the characters.
 *
 * Order does not matter — markWords sorts by length and takes the longest match
 * at each position — but listing words first states the intent.
 */
export const HIGHLIGHT_TARGETS: string[] = [
  ...COMMON_WORDS_ZH.map((w) => w.hanzi),
  ...TOP_CHARS_ZH.map((c) => c.hanzi),
]
